import {
  Component,
  Inject,
  Input,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Observable } from 'rxjs';
import { SavedTrackRef } from '../../../shared/contracts/playback-controller';
import { ToolbarDrawerService } from '../../../shared/toolbar/toolbar-drawer.service';
import { ToolbarControlContext, TOOLBAR_CONTROL_CONTEXT } from '../../../shared/contracts/toolbar-control.tokens';
import { VoyageRadarPlaybackController } from '../../voyage-radar-playback.controller';
import {
  VoyageTrackController,
  VoyageTrackState,
  VOYAGE_TRACK_SPANS,
} from '../track.controller';
import { VOYAGE_TRACK_CREDITS } from 'src/app/services/operators/voyageradar-credit-actions';
import { voyageSpendBlocker } from '../../voyage-credits.model';
import { isPathlessTrack } from '../../panels/track-history/voyage-track-history.model';
import { BaseControl } from '../../../shared/toolbar/controls/base-control';

/**
 * Whether the map holds vessels that a track bar's fleet controls would act on.
 *
 * An UNKNOWN count (null) counts as yes: the map has simply not reported yet,
 * and hiding a working control is the worse of the two mistakes. Shared by the
 * dim and the grouping buttons so the rule is stated once.
 */
function hasOtherVessels(s: VoyageTrackState): boolean {
  return s.othersOnMap == null || s.othersOnMap > 0;
}

/**
 * Track-slot control plugins — Voyage Radar's per-vessel path playback bar.
 *
 * These are the only operator-specific controls that talk to
 * `VoyageTrackController` rather than the generic contract, so they cast (or,
 * for the live-slot entry button, inject) it directly. Everything else in the
 * track bar is a stock transport control driven purely through `state$`.
 */

/**
 * Contents of the trace drawer: every way to load a path for the selected
 * vessel. Deliberately NOT in the map popup — that popup opens on every vessel
 * click and has to stay a compact info card.
 *
 * Projected into `app-toolbar-drawer` by both the live-slot entry button and the
 * track-slot source button, so the options are identical whether you are
 * starting a trace or changing one.
 *
 * Picking a span and spending the credit are two separate clicks. A chip only
 * SELECTS — the operator is not called until the Trace button below it is
 * pressed. Those two clicks ARE the spend gate (there is no confirmation dialog
 * behind them), which is why the cost line sits with the button rather than
 * anywhere else in the card.
 */
@Component({
  selector: 'app-track-options-panel',
  template: `
    <ng-container *ngIf="controller.state$ | async as s">
      <!-- No hull chosen. The live-slot button that opens this panel is a
           STANDING control now, so this state is reachable — it was not while
           the button appeared only once something was selected. The price rides
           on the drawer subtitle, so the cost is known before a hull is. -->
      <div class="tb-drawer__hint" *ngIf="!s.activeMmsi">
        Select a vessel on the map to trace its path.
      </div>

      <ng-container *ngIf="s.activeMmsi">
        <ng-container *ngIf="s.savedTracks?.length">
          <span class="tb-drawer__label">Saved tracks</span>
          <div class="tb-drawer__chips">
            <!-- A single-position track is dead here for the same reason it is
                 dead in the sidebar: the transport needs two points, and a
                 chip that answers with an error dialog is worse than one that
                 does not press. -->
            <button type="button" class="tb-chip tb-chip--free" *ngFor="let t of s.savedTracks"
                    [disabled]="s.trackLoading || pathless(t)"
                    (click)="replay(t.trackId)"
                    [matTooltip]="pathless(t)
                      ? 'Only one position was recorded in this window — there is no path to replay'
                      : 'Replay a track this task already holds'">
              <!-- The reason rides in the chip's own trailing slot rather than
                   in the tooltip: a disabled button never fires the hover that
                   would show one — the same reason the Trace button below
                   carries its prompt inline. -->
              {{ t.spanLabel || 'track'
              }}<em *ngIf="pathless(t); else ptsLabel">&nbsp;&middot;&nbsp;no path</em>
              <ng-template #ptsLabel>
                <em *ngIf="t.count">&nbsp;&middot;&nbsp;{{ t.count }} pts</em>
              </ng-template>
            </button>
          </div>
        </ng-container>

        <!-- Deadness via blocker(), never an inline comparison: an empty wallet
             is a standing condition of these chips and the cost line below
             already states the price and the balance, so a dead chip is the
             whole explanation. An ended TOI deliberately does NOT reach here —
             it leaves the chips live so the press can be answered with a modal
             that names the refund. An UNKNOWN balance (null) stays live too —
             null < cost is TRUE in JS, so a naive test would refuse a user the
             server would allow. -->
        <span class="tb-drawer__label">Trace period</span>
        <div class="tb-drawer__chips">
          <button type="button" class="tb-chip" *ngFor="let span of spans"
                  [class.tb-chip--on]="selected === span.minutes"
                  [attr.aria-pressed]="selected === span.minutes"
                  [disabled]="s.trackLoading || !!blocker(s)"
                  (click)="pick(span.minutes)">{{ span.label }}</button>
        </div>

        <!-- Below the chips, not above: it acts on what they select, and the
             order on screen is the order of the two steps. -->
        <!-- The prompt rides in the button's own trailing slot rather than in a
             tooltip: it is needed exactly when the button is disabled, and a
             disabled button never fires the hover that would show one. -->
        <button type="button" class="tb-drawer__primary tb-drawer__primary--trace"
                [disabled]="selected === null || s.trackLoading || !!blocker(s)"
                matTooltip="Fetch this vessel's recorded track from the operator"
                (click)="trace()">
          <mat-icon>timeline</mat-icon>
          <span class="tb-drawer__primary-label">Trace</span>
          <em>{{ selectedLabel || 'select a period' }}</em>
        </button>

        <div class="tb-drawer__cost">
          <ng-container *ngIf="blocker(s) as why; else haveCredits">
            {{ why }}
          </ng-container>
          <ng-template #haveCredits>
            Uses {{ trackCostOf(s) }} credits
            <ng-container *ngIf="s.creditsRemaining != null">
              &middot; {{ s.creditsRemaining }} remaining
            </ng-container>
            &middot; max 90 days
          </ng-template>
        </div>
      </ng-container>
    </ng-container>`,
  // No stylesheet of its own: this content is projected into app-toolbar-drawer,
  // which owns the shared .tb-drawer__* / .tb-chip vocabulary via ::ng-deep.
})
export class TrackOptionsPanelComponent {
  @Input() controller!: VoyageTrackController;
  readonly spans = VOYAGE_TRACK_SPANS;

  /**
   * What a track costs on THIS sensor, as reported with the balance. The
   * shipped constant is the fallback for a controller that has not reported
   * yet — never the price when the catalogue has one.
   */
  trackCostOf(s: any): number {
    return Number(s?.trackCost) > 0 ? Number(s.trackCost) : VOYAGE_TRACK_CREDITS;
  }

  /**
   * Whether a saved track holds too few positions to draw. Delegated to the
   * sidebar's rule rather than restated — the chip and the row must never
   * disagree about whether the same record can be replayed.
   */
  pathless(track: SavedTrackRef): boolean {
    return isPathlessTrack(track);
  }

  /**
   * What these controls must go DEAD for, or null when they stay live — an
   * empty wallet, and deliberately NOT an ended TOI, which stays pressable so
   * `confirmFullTrack` can answer the press with the modal. See
   * `voyageSpendBlocker`. Unknown balance (null) allows; see `canAfford`.
   */
  blocker(s: any): string | null {
    return voyageSpendBlocker({
      action: 'track',
      cost: this.trackCostOf(s),
      remaining: s?.creditsRemaining ?? null,
      toiEnded: !!s?.toiEnded,
    });
  }

  /**
   * Chosen span in minutes, or null while nothing is picked — which is what
   * disables the Trace button. Deliberately NOT pre-filled: every value here
   * costs a credit, so the span that gets bought has to be one the user named.
   *
   * Panel-local rather than controller state: the drawer builds a fresh
   * instance each time it opens, so a span abandoned last time never carries
   * over into the next trace.
   */
  selected: number | null = null;

  constructor(private drawer: ToolbarDrawerService) {}

  get selectedLabel(): string | null {
    return this.spans.find((s) => s.minutes === this.selected)?.label ?? null;
  }

  /** Chip click. Re-clicking the chosen span clears it, like any toggle. */
  pick(minutes: number): void {
    this.selected = this.selected === minutes ? null : minutes;
  }

  /**
   * Spend the credit. Dismisses the drawer first, so the fetch it started is
   * visible on the map rather than behind this card — and so the button cannot
   * be pressed a second time before `trackLoading` has come back to disable it.
   */
  trace(): void {
    if (this.selected === null) return;
    const minutes = this.selected;
    this.drawer.close();
    this.controller.requestFullTrack(minutes);
  }

  /** Replay a track this task already owns — free, no operator call. */
  replay(trackId: string): void {
    this.drawer.close();
    this.controller.requestSavedTrack(trackId);
  }
}

@Component({
  selector: 'app-track-close-control',
  template: `
    <button type="button" class="tb-btn" (click)="ctrl.clearActiveVessel()"
            aria-label="Close track"
            matTooltip="Take this track off the map" matTooltipPosition="above">
      <mat-icon class="material-icons-outlined">close</mat-icon>
    </button>`,
})
export class TrackCloseControlComponent extends BaseControl {
  constructor(@Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext) {
    super();
  }
}

/**
 * Track slot: fade the rest of the fleet, or bring it back.
 *
 * On by default — a single path drawn under a few hundred live hulls is
 * unreadable. But the traffic around a track is frequently the point of buying
 * one (who else was there, and when), and at 12% opacity those hulls are neither
 * legible nor comfortably clickable. This hands that decision back rather than
 * making the user close the track to see the fleet.
 *
 * ONE glyph naming the effect, lit while it is on — the same grammar as the
 * labels button beside it, which is why it is not an eye swapping between
 * `visibility` and `visibility_off`. In a bar whose every other control acts on
 * the track, an eye reads as "hide the track"; a blur reads as what it is. NOT
 * `layers` either: the map already carries Leaflet's own layer control, and that
 * IS the stack-of-layers glyph.
 *
 * Renders NOTHING when there are no other vessels on the map — a track replayed
 * from the Vessel Tracks panel can be the only thing drawn. There the dim has
 * nothing to act on, and the button sat lit (`dimOthers` defaults on) claiming a
 * fleet was faded that was never there. Hidden rather than disabled, because a
 * disabled button never fires the hover that would let a tooltip explain itself.
 *
 * Note this is NOT the rule the trace entry button follows — that one stands on
 * the bar with nothing selected and lets its drawer do the explaining. The two
 * differ because their empty states differ: a drawer has room to say "select a
 * vessel", and a bare toggle has nowhere to put that sentence.
 *
 * The PREFERENCE is left untouched while it is away, so pulling a snapshot
 * mid-track brings the button back in the state the user last left it.
 */
@Component({
  selector: 'app-track-dim-control',
  template: `
    <ng-container *ngIf="state$ | async as s">
      <button *ngIf="hasOthers(s)" type="button" class="tb-btn"
              [class.tb-btn--active]="s.dimOthers"
              (click)="ctrl.toggleDimOthers()"
              aria-label="Toggle surrounding vessels"
              [matTooltip]="s.dimOthers
                ? 'Show the other vessels'
                : 'Fade the other vessels'"
              matTooltipPosition="above">
        <mat-icon class="material-icons-outlined">blur_on</mat-icon>
      </button>
    </ng-container>`,
})
export class TrackDimControlComponent extends BaseControl {
  constructor(
    @Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext,
    /**
     * The instance the track slot's host is driven by. NOT the
     * `providedIn: 'root'` one: the map component ELEMENT-provides this
     * controller (see the providers array and its bug note in
     * leaflet-newcollections.component.ts), and this control's injector chain
     * runs through that component's view — so DI resolves the component's
     * instance, the same one bound to the bar. Injected for the TYPE
     * (`VoyageTrackState`); a control hosted outside that view would silently
     * get a different instance, which is the trap the providers note records.
     */
    private track: VoyageTrackController,
  ) {
    super();
  }

  /**
   * Narrowed to the track state its template reads (`dimOthers`, `othersOnMap`
   * moved off the base contract in the phase-0 extraction). Same instance as
   * `context.controller` on this slot; the injection is for the TYPE.
   */
  override get state$(): Observable<VoyageTrackState> {
    return this.track.state$;
  }

  /** Whether the map holds anything for the dim to fade. */
  hasOthers(s: VoyageTrackState): boolean {
    return hasOtherVessels(s);
  }
}

/**
 * Track slot: group the surrounding traffic into count bubbles, or draw every
 * hull. The live and playback bars have carried this for as long as they have
 * existed; this is the same control, on the bar a track puts in their place.
 *
 * It appears only once the fleet dim is OFF, because the two never stack: while
 * the dim holds the other hulls at 0.12 there are no clustering candidates at
 * all, so no bubbles can form (see `applyTypeFilterToLive` in the map
 * controller). Undimming a busy AOI is precisely when a thousand overlapping
 * arrows need grouping, and until now the only way to reach the toggle was to
 * close the track and go back to the live bar.
 *
 * Pairing it with the dim also keeps the bar honest in the other direction: it
 * is never on screen in a state where pressing it would change nothing.
 *
 * NOTE: clustering is a property of the AOI SNAPSHOT, not of the track, so —
 * like the live slot's trace entry button, and for the same reason — this control
 * injects the controller that owns it rather than using `context.controller`,
 * which in this slot is the track. The gate reads the TRACK's state (its dim,
 * its view of the fleet); the button reads and toggles the snapshot's.
 */
@Component({
  selector: 'app-track-cluster-control',
  template: `
    <ng-container *ngIf="state$ | async as s">
      <ng-container *ngIf="aoi.state$ | async as c">
        <button *ngIf="canGroup(s)" type="button" class="tb-btn"
                [class.tb-btn--active]="c.clusterVessels"
                (click)="aoi.toggleClusters()"
                aria-label="Toggle vessel grouping"
                [matTooltip]="c.clusterVessels
                  ? 'Show every vessel'
                  : 'Group nearby vessels'"
                matTooltipPosition="above">
          <mat-icon class="material-icons-outlined">bubble_chart</mat-icon>
        </button>
      </ng-container>
    </ng-container>`,
})
export class TrackClusterControlComponent extends BaseControl {
  constructor(
    @Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext,
    /** Owns `clusterVessels` and the display pass that acts on it. */
    public aoi: VoyageRadarPlaybackController,
    /**
     * The instance the track slot's host is driven by. NOT the
     * `providedIn: 'root'` one: the map component ELEMENT-provides this
     * controller (see the providers array and its bug note in
     * leaflet-newcollections.component.ts), and this control's injector chain
     * runs through that component's view — so DI resolves the component's
     * instance, the same one bound to the bar. Injected for the TYPE
     * (`VoyageTrackState`); a control hosted outside that view would silently
     * get a different instance, which is the trap the providers note records.
     */
    private trackCtrl: VoyageTrackController,
  ) {
    super();
  }

  /** Narrowed for the gate's reads of `dimOthers`/`othersOnMap` — see TrackDim. */
  override get state$(): Observable<VoyageTrackState> {
    return this.trackCtrl.state$;
  }

  /** There is a fleet on the map, and it is not currently faded out. */
  canGroup(s: VoyageTrackState): boolean {
    return !s.dimOthers && hasOtherVessels(s);
  }
}

/**
 * Which hull, and which kind of path — as a colour dot and a flag, not a name.
 *
 * The name is in the tooltip because the sidebar's ON MAP bar is spelling it out
 * two inches away, and a 200px vessel name in the middle of a transport pushes
 * the scrub rail off the chart. The dot is the vessel's own colour, so the bar,
 * the halo, the trail and the sidebar row are visibly one object.
 */
@Component({
  selector: 'app-track-title-control',
  template: `
    <span class="tb-vessel" *ngIf="state$ | async as s"
          [matTooltip]="tooltip(s)" matTooltipPosition="above">
      <span class="tb-vessel__dot" [style.background]="s.activeVesselColor || '#6B7280'"
            [style.color]="s.activeVesselColor || '#6B7280'"></span>
      <span class="tb-vessel__flag">{{ kind(s) }}</span>
    </span>`,
})
export class TrackTitleControlComponent extends BaseControl {
  constructor(
    @Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext,
    /**
     * The instance the track slot's host is driven by. NOT the
     * `providedIn: 'root'` one: the map component ELEMENT-provides this
     * controller (see the providers array and its bug note in
     * leaflet-newcollections.component.ts), and this control's injector chain
     * runs through that component's view — so DI resolves the component's
     * instance, the same one bound to the bar. Injected for the TYPE
     * (`VoyageTrackState`); a control hosted outside that view would silently
     * get a different instance, which is the trap the providers note records.
     */
    private track: VoyageTrackController,
  ) {
    super();
  }

  /** Narrowed for the badge's reads of the track fields — see TrackDim. */
  override get state$(): Observable<VoyageTrackState> {
    return this.track.state$;
  }

  /**
   * "90 D" — the span this track was BOUGHT for, which is what `trackKind` on
   * the sidebar's ON MAP bar and the saved-track chips both name it.
   *
   * Deliberately the requested span rather than one measured off the loaded
   * points. A 90-day fetch for a vessel that only broadcast over its last three
   * days came back reading "3 D" here while the sidebar two inches away said
   * "90 D" — two numbers for one path, and the one in the toolbar was not the
   * one the operator had chosen and paid for. Where the returned positions
   * actually start and end is in the tooltip, which can afford to say both.
   *
   * Falls back to the measured span only when neither the stored record nor the
   * request is known — an older track record carrying no `spanLabel`.
   */
  kind(s: VoyageTrackState): string {
    const label = this.recordSpan(s) ?? this.requestedSpan(s) ?? this.coverage(s);
    return label ? label.toUpperCase() : 'TRACK';
  }

  /** The span on the stored record — the same string the sidebar reads. */
  private recordSpan(s: VoyageTrackState): string | null {
    const rec = (s.savedTracks ?? []).find(
      (t) => t.trackId && t.trackId === s.activeTrackId
    );
    return String(rec?.spanLabel ?? '').trim() || null;
  }

  /** The span the trace drawer asked for — set only for a paid full fetch. */
  private requestedSpan(s: VoyageTrackState): string | null {
    const minutes = Number(s.trackSpanMinutes);
    if (!isFinite(minutes) || minutes <= 0) return null;
    const known = VOYAGE_TRACK_SPANS.find((span) => span.minutes === minutes);
    if (known) return known.label;
    if (minutes < 60) return `${Math.round(minutes)} min`;
    if (minutes < 2880) return `${Math.round(minutes / 60)} h`;
    return `${Math.round(minutes / 1440)} d`;
  }

  /** What the returned positions actually cover, measured off the loaded steps. */
  private coverage(s: VoyageTrackState): string | null {
    const labels = s.stepLabels ?? [];
    const from = new Date(labels[0] ?? '').getTime();
    const to = new Date(labels[labels.length - 1] ?? '').getTime();
    const ms = to - from;
    if (!isFinite(ms) || ms <= 0) return null;
    const hours = ms / 3600000;
    if (hours < 1) return `${Math.max(1, Math.round(ms / 60000))} min`;
    if (hours < 48) return `${Math.round(hours)} h`;
    return `${Math.round(hours / 24)} d`;
  }

  /**
   * The badge cannot carry both the span bought and the span received, and when
   * a vessel went dark for most of the window those two differ a lot — so the
   * tooltip states the coverage whenever it is not simply a restatement of the
   * badge.
   */
  tooltip(s: VoyageTrackState): string {
    const name = s.activeVesselName || s.activeMmsi || 'Vessel';
    const mmsi = s.activeVesselName && s.activeMmsi ? ` · ${s.activeMmsi}` : '';
    const parts = [`${name}${mmsi}`, this.sourceLabel(s)];
    // Only claim "requested" for a span that really was one: with no record and
    // no request the badge is already showing the coverage, and calling that a
    // request would put the wrong number behind the word.
    const asked = this.recordSpan(s) ?? this.requestedSpan(s);
    const covered = this.coverage(s);
    if (asked) parts.push(`${asked} requested`);
    if (covered && covered.toLowerCase() !== (asked ?? '').toLowerCase()) {
      parts.push(`positions span ${covered}`);
    }
    parts.push(`${s.stepCount} pts`);
    return parts.join(' · ');
  }

  sourceLabel(s: VoyageTrackState): string {
    return s.trackSource === 'saved' ? 'Saved track' : 'Full track';
  }
}

/** Track slot: change the loaded path (replay a saved one, or buy another span). */
@Component({
  selector: 'app-track-source-control',
  template: `
    <ng-container *ngIf="state$ | async as s">
      <button #trigger type="button" class="tb-btn"
              [class.tb-btn--active]="drawer.isOpen(panel)"
              [disabled]="s.trackLoading"
              (click)="drawer.toggle(panel, vcr, trigger)"
              aria-label="Change track"
              matTooltip="Load a different path for this vessel" matTooltipPosition="above">
        <!-- Disabled on the truth, spun on the delayed flag: the gate has to
             hold from the first millisecond so a second press cannot start a
             second load, but a spin that appears and stops inside 100 ms — which
             a short track on a good connection does — reads as a glitch. See
             setTrackLoading on the track controller. -->
        <mat-icon class="material-icons-outlined" [class.tb-spin]="s.trackLoadingVisible">history</mat-icon>
      </button>

    </ng-container>

    <!-- Declared OUTSIDE the *ngIf on purpose: the overlay holds this
         TemplateRef, so destroying its declaring view while the drawer is open
         would leave a detached panel on screen. -->
    <ng-template #panel>
      <app-toolbar-drawer *ngIf="state$ | async as s"
                          [title]="s.activeVesselName || s.activeMmsi || 'Vessel track'"
                          [subtitle]="s.activeVesselName ? s.activeMmsi : null"
                          [accent]="s.activeVesselColor"
                          (close)="drawer.close()">
        <app-track-options-panel [controller]="track"></app-track-options-panel>
      </app-toolbar-drawer>
    </ng-template>`,
})
export class TrackSourceControlComponent extends BaseControl implements OnDestroy {
  @ViewChild('panel') panel!: TemplateRef<any>;

  constructor(
    @Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext,
    /**
     * The track slot's host IS driven by this controller — DI hands back the
     * map component's ELEMENT-provided instance (not the `providedIn: 'root'`
     * fallback; see the providers note in leaflet-newcollections.component.ts),
     * TYPED, where the getter this replaces cast `context.controller`. The
     * injection is for the type; the instance is identical because this
     * control only ever renders inside that component's view.
     */
    public track: VoyageTrackController,
    public drawer: ToolbarDrawerService,
    public vcr: ViewContainerRef,
  ) {
    super();
  }

  /** Narrowed for the template's `trackLoading`/`trackLoadingVisible` reads. */
  override get state$(): Observable<VoyageTrackState> {
    return this.track.state$;
  }

  ngOnDestroy(): void {
    // The toolbar swaps slots, so this control can vanish while its drawer is
    // open — that would leave the overlay floating with no trigger.
    this.drawer.closeIfOpen(this.panel);
  }
}

/**
 * Live-slot entry button — clicking it opens the trace drawer above the toolbar.
 *
 * A STANDING control, on the same terms as port calls and nearby vessels: it is
 * on the bar whether or not a vessel is selected, and the drawer is what says
 * "select a vessel" when none is. It used to render nothing until something was
 * selected, which made the paid per-vessel actions a set of three that appeared
 * and vanished at different moments — and left the bar with no way to say that
 * tracing is a thing this sensor can do at all. A control that is only ever
 * visible once you already know to look for it teaches nobody.
 *
 * The drawer carries the price in its subtitle with no hull chosen, so the cost
 * is legible before a selection, exactly as its two siblings do it.
 *
 * An icon button like every other control on the bar, which it did not used to
 * be: it was a filled accent pill reading "Trace", on the argument that tracing
 * spends a fetch credit and an unlabelled icon does not get clicked. That
 * argument stopped distinguishing it once port calls and nearby vessels landed
 * beside it — three paid controls in one bar, one of them wearing a pill, which
 * read as a hierarchy that does not exist. The price is stated where the spend
 * actually happens, on each drawer's own primary button and cost line, and the
 * tooltip here names the hull.
 *
 * It carries nothing else. The vessel's colour used to ride here as a dot, but
 * a filled disc reads as a status light rather than an identity — a red hull
 * made the one paid button look like it was recording — and every other control
 * in the bar is monochrome chrome. Which hull this applies to is answered twice
 * already: the map halos it, and the drawer this opens is titled with its name.
 *
 * NOTE: this control lives in the LIVE slot, whose host is bound to the AOI
 * snapshot controller — so `context.controller` is the wrong one here. It
 * therefore injects `VoyageTrackController` straight from DI (the host's
 * per-control injector chains to the parent). Deliberate, Voyage-Radar-specific
 * exception; the alternative is a second live host and a duplicated shell.
 */
@Component({
  selector: 'app-track-entry-control',
  template: `
    <!-- No *ngIf on the button. The wrapper is how the tooltip reaches the
         selection, not a visibility condition: a BehaviorSubject emits
         synchronously, so this always renders and the button stands whether or
         not a hull is chosen — the same shape as its port-call and nearby
         siblings. -->
    <ng-container *ngIf="state$ | async as s">
      <button #trigger type="button" class="tb-btn"
              [class.tb-btn--active]="drawer.isOpen(panel)"
              [disabled]="s.trackLoading"
              (click)="drawer.toggle(panel, vcr, trigger)"
              aria-label="Trace vessel path"
              [matTooltip]="tooltip(s)"
              matTooltipPosition="above">
        <!-- Delayed flag, not the gate — see the note on the track-source button. -->
        <mat-icon class="material-icons-outlined" [class.tb-spin]="s.trackLoadingVisible">timeline</mat-icon>
      </button>
    </ng-container>

    <!-- Declared OUTSIDE the wrapper, like every drawer panel here: this button
         no longer disappears with the selection, but an open overlay must never
         be left holding a TemplateRef whose declaring view was destroyed. -->
    <ng-template #panel>
      <app-toolbar-drawer *ngIf="state$ | async as s"
                          [title]="drawerTitle(s)"
                          [subtitle]="drawerSubtitle(s)"
                          [accent]="s.activeVesselColor"
                          (close)="drawer.close()">
        <app-track-options-panel [controller]="track"></app-track-options-panel>
      </app-toolbar-drawer>
    </ng-template>`,
})
export class TrackEntryControlComponent extends BaseControl implements OnDestroy {
  @ViewChild('panel') panel!: TemplateRef<any>;

  constructor(
    @Inject(TOOLBAR_CONTROL_CONTEXT) public context: ToolbarControlContext,
    public track: VoyageTrackController,
    public drawer: ToolbarDrawerService,
    public vcr: ViewContainerRef,
  ) {
    super();
  }

  /** Reads the TRACK controller's state, not the live slot's controller. */
  override get state$(): Observable<VoyageTrackState> {
    return this.track.state$;
  }

  /** What a track costs on THIS sensor, as reported with the balance. */
  private costOf(s: VoyageTrackState): number {
    return Number(s?.trackCost) > 0 ? Number(s.trackCost) : VOYAGE_TRACK_CREDITS;
  }

  private costLabel(s: VoyageTrackState): string {
    const n = this.costOf(s);
    return `${n} ${n === 1 ? 'credit' : 'credits'}`;
  }

  /** Names the hull once there is one; says what the control is when there is not. */
  tooltip(s: VoyageTrackState): string {
    const hull = s.activeVesselName || s.activeMmsi;
    return hull ? `Trace ${hull}` : `Trace a vessel's path · ${this.costLabel(s)}`;
  }

  drawerTitle(s: VoyageTrackState): string {
    return s.activeVesselName || s.activeMmsi || 'Vessel track';
  }

  /**
   * The MMSI once a hull is chosen, the PRICE before then — the same rule the
   * port-call and nearby drawers follow, and the reason the cost of tracing is
   * legible without a selection now that the button is always on the bar.
   */
  drawerSubtitle(s: VoyageTrackState): string | null {
    if (!s.activeMmsi) return `${this.costLabel(s)} per track`;
    return s.activeVesselName ? s.activeMmsi : null;
  }

  ngOnDestroy(): void {
    this.drawer.closeIfOpen(this.panel);
  }
}
